import { Component, ErrorInfo, ReactNode } from "react";
import { errorEmitter } from "../utils/errorEmitter";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

function safeStringify(value: any, space = 2): string {
  const seen = new WeakSet();
  try {
    return JSON.stringify(
      value,
      (_key, val) => {
        if (val instanceof Error) {
          return { name: val.name, message: val.message, stack: val.stack };
        }
        if (typeof val === "object" && val !== null) {
          if (seen.has(val)) return "[Circular]";
          seen.add(val);
        }
        if (typeof val === "function") return `[Function ${val.name || "anonymous"}]`;
        if (typeof val === "undefined") return "[undefined]";
        return val;
      },
      space,
    );
  } catch {
    return String(value);
  }
}

class ErrorHandler extends Component<Props, State> {
  state: State = { hasError: false };

  private lastError: any = null;
  private lastErrorInfo: ErrorInfo | null = null;
  private unsubscribe?: () => void;

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[ErrorBoundary]", error, errorInfo);
    this.lastError = error;
    this.lastErrorInfo = errorInfo;
  }

  componentDidMount() {
    this.unsubscribe = errorEmitter.subscribe((error: any) => {
      console.error("[ErrorHandler] global error caught", error);
      this.lastError = error;
      this.setState({ hasError: true });
    });
  }

  componentWillUnmount() {
    this.unsubscribe?.();
  }

  private buildReport(): string {
    const error = this.lastError;
    const errorInfo = this.lastErrorInfo;
    const lines: string[] = [];
    const push = (label: string, value: any) => {
      lines.push(`## ${label}`);
      lines.push(typeof value === "string" ? value : safeStringify(value));
      lines.push("");
    };

    lines.push("==================================================");
    lines.push("  오류 리포트 (Error Report)");
    lines.push("==================================================");
    lines.push("");

    // 1) 기본 환경 정보
    push("발생 시각", new Date().toISOString());
    push("페이지 URL", typeof window !== "undefined" ? window.location.href : "N/A");
    push("User Agent", typeof navigator !== "undefined" ? navigator.userAgent : "N/A");

    if (!error) {
      push("에러 정보", "캡처된 에러 객체가 없습니다. (렌더 단계 이전 또는 정보 유실)");
      return lines.join("\n");
    }

    // 2) 에러 메시지 / 스택
    push("에러 이름", error.name ?? "N/A");
    push("에러 메시지", error.message ?? String(error));
    if (error.stack) push("스택 트레이스", error.stack);

    // 3) React 컴포넌트 스택
    if (errorInfo?.componentStack) push("컴포넌트 스택", errorInfo.componentStack);

    // 4) 요청 정보 (axios 등의 error.config)
    const config = error.config;
    if (config) {
      push("요청 메서드", (config.method || "").toUpperCase());
      push("요청 URL", config.baseURL ? `${config.baseURL}${config.url ?? ""}` : config.url);
      if (config.headers) push("요청 헤더", config.headers);
      if (config.params) push("요청 쿼리 파라미터 (params)", config.params);
      if (config.data !== undefined) {
        let payload = config.data;
        if (typeof payload === "string") {
          try {
            payload = JSON.parse(payload);
          } catch {}
        }
        push("요청 본문 (payload)", payload);
      }
      if (config.timeout) push("요청 타임아웃(ms)", String(config.timeout));
    }

    // 5) 응답 정보 (axios 등의 error.response)
    const response = error.response;
    if (response) {
      push("응답 상태 코드", `${response.status ?? ""} ${response.statusText ?? ""}`.trim());
      if (response.headers) push("응답 헤더", response.headers);
      if (response.data !== undefined) push("응답 본문", response.data);
    } else if (error.request) {
      push("요청 상태", "요청은 전송되었으나 응답을 받지 못했습니다. (네트워크 오류 또는 타임아웃)");
    }

    // 6) 그 외 부가 정보
    if (error.code) push("에러 코드", error.code);
    if (typeof error === "object") {
      const handled = new Set(["name", "message", "stack", "config", "response", "request", "code"]);
      const extras: Record<string, any> = {};
      Object.keys(error).forEach((k) => {
        if (!handled.has(k)) extras[k] = error[k];
      });
      if (Object.keys(extras).length > 0) push("기타 정보", extras);
    }

    // 7) 원본 에러 객체 전체 (디버깅용)
    push("원본 에러 객체(전체)", error);

    return lines.join("\n");
  }

  private handleDownload = () => {
    const report = this.buildReport();
    const blob = new Blob([report], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    const a = document.createElement("a");
    a.href = url;
    a.download = `error-report-${ts}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "100vh",
              padding: 40,
              backgroundColor: "var(--dk-surface-2, #f5f5f7)",
              fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', sans-serif",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                backgroundColor: "#fef2f2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 24,
              }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ef4444"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 9v4" />
                <path d="M12 17h.01" />
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              </svg>
            </div>

            <h2
              style={{
                fontSize: 20,
                fontWeight: 600,
                color: "var(--dk-text, #1d1d1f)",
                margin: "0 0 8px",
                letterSpacing: "-0.01em",
              }}
            >
              일시적인 오류가 발생했습니다
            </h2>

            <p
              style={{
                fontSize: 14,
                color: "var(--dk-text-2, #6e6e73)",
                margin: "0 0 28px",
                maxWidth: 360,
                lineHeight: 1.5,
              }}
            >
              페이지를 표시하는 중 문제가 발생했습니다.
              <br />
              새로고침 후에도 문제가 계속되면 아래 오류 내용을 저장해
              <br />
              관리자에게 전달해 주세요.
            </p>

            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: "10px 24px",
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#fff",
                  backgroundColor: "#2563eb",
                  border: "none",
                  borderRadius: 14,
                  cursor: "pointer",
                  transition: "background-color 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#1d4ed8";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#2563eb";
                }}
              >
                새로고침
              </button>

              <button
                onClick={this.handleDownload}
                style={{
                  padding: "10px 24px",
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#2563eb",
                  backgroundColor: "var(--dk-surface, #fff)",
                  border: "1px solid var(--dk-border, #d2d2d7)",
                  borderRadius: 14,
                  cursor: "pointer",
                  transition: "background-color 0.15s ease, border-color 0.15s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f5f5f7";
                  e.currentTarget.style.borderColor = "#b0b0b8";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#fff";
                  e.currentTarget.style.borderColor = "#d2d2d7";
                }}
              >
                오류 내용 저장 (.txt)
              </button>
            </div>
          </div>
        )
      );
    }
    return this.props.children;
  }
}

export default ErrorHandler;
